
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';

interface FavoritesContextType {
  favorites: number[];
  addFavorite: (id: number) => void;
  removeFavorite: (id: number) => void;
  isFavorite: (id: number) => boolean;
}

const FavoritesContext = createContext<FavoritesContextType>({
  favorites: [],
  addFavorite: () => {},
  removeFavorite: () => {},
  isFavorite: () => false
});

export const useFavorites = () => useContext(FavoritesContext);

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider = ({ children }: FavoritesProviderProps) => {
  const [favorites, setFavorites] = useState<number[]>([]);
  const { trackExhibitFavorite } = useAnalyticsTracking();
  
  // Load favorites from localStorage on initial render
  useEffect(() => {
    const savedFavorites = localStorage.getItem('museum_favorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error('Error loading favorites from localStorage:', error);
      }
    }
  }, []);
  
  // Save favorites to localStorage when they change
  useEffect(() => {
    localStorage.setItem('museum_favorites', JSON.stringify(favorites));
  }, [favorites]);
  
  const addFavorite = (id: number) => {
    if (!favorites.includes(id)) {
      setFavorites([...favorites, id]);
      trackExhibitFavorite(id.toString(), 'add');
    }
  };
  
  const removeFavorite = (id: number) => {
    setFavorites(favorites.filter(favId => favId !== id));
    trackExhibitFavorite(id.toString(), 'remove');
  };
  
  const isFavorite = (id: number) => {
    return favorites.includes(id);
  };
  
  return (
    <FavoritesContext.Provider value={{ favorites, addFavorite, removeFavorite, isFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
};
